/**
 * Axios client for the user PWA. Mirrors the admin client:
 * envelope unwrapping, bearer token injection, typed ApiError on failure.
 */
import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/auth';

export class ApiError extends Error {
  constructor(
    public code: number,
    message: string,
    public fields?: Record<string, string>,
    public errorKey?: string,
  ) {
    super(message);
  }
}

const instance: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? '/',
  timeout: 30_000,
});

const refreshClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? '/',
  timeout: 30_000,
});

type RetryableRequestConfig = AxiosRequestConfig & { _retry?: boolean };

instance.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

instance.interceptors.response.use(
  (res) => {
    const body = res.data;
    if (body && typeof body === 'object' && 'code' in body) {
      if (body.code === 0) {
        return { ...res, data: body.data };
      }
      throw new ApiError(body.code, body.msg ?? 'api error', body.fields, body.error_key);
    }
    return res;
  },
  async (err: AxiosError<{ code?: number; msg?: string; fields?: Record<string, string>; error_key?: string }>) => {
    const original = err.config as RetryableRequestConfig | undefined;
    if (err.response?.status === 401) {
      const refreshToken = useAuthStore.getState().refreshToken;
      const url = original?.url ?? '';
      const isRefreshRequest = url.includes('/api/auth/refresh') || url.includes('/auth/refresh');
      const isPublicAuthRequest =
        url.includes('/api/auth/login') ||
        url.includes('/api/auth/wallet-login') ||
        url.includes('/api/auth/wallet-challenge') ||
        url.includes('/api/auth/register');

      if (refreshToken && original && !original._retry && !isRefreshRequest && !isPublicAuthRequest) {
        original._retry = true;
        try {
          const refreshed = await refreshClient.post<{
            code: number;
            msg: string;
            data: { access_token: string; refresh_token: string };
          }>('/api/auth/refresh', { refresh_token: refreshToken });
          const tokens = refreshed.data.data;
          useAuthStore.getState().setTokens(tokens.access_token, tokens.refresh_token);
          original.headers = {
            ...(original.headers ?? {}),
            Authorization: `Bearer ${tokens.access_token}`,
          };
          return instance(original);
        } catch {
          useAuthStore.getState().clear();
        }
      }
      useAuthStore.getState().clear();
    }
    const body = err.response?.data;
    if (body && typeof body === 'object' && 'code' in body) {
      throw new ApiError(
        body.code ?? err.response!.status,
        body.msg ?? err.message,
        body.fields,
        body.error_key,
      );
    }
    throw new ApiError(err.response?.status ?? 0, err.message);
  },
);

export const api = {
  get: <T = unknown>(url: string, params?: unknown, config?: AxiosRequestConfig) =>
    instance.get<T>(url, { params, ...config }).then((r) => r.data as unknown as T),
  post: <T = unknown>(url: string, body?: unknown) =>
    instance.post<T>(url, body).then((r) => r.data as unknown as T),
  patch: <T = unknown>(url: string, body?: unknown) =>
    instance.patch<T>(url, body).then((r) => r.data as unknown as T),
};
